import { useDeepEqualMemo } from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo, useState } from 'react';
import type { DeviceType, NativeDeviceType } from '~/contracts/common';
import type { DeepPartial } from '~/types';

export type ScreenSize = {
  height: number;
  width: number;
};

export type ContextType = {
  browserName: string;
  deviceType: DeviceType;
  nativeDeviceType: NativeDeviceType;
  screenSize: ScreenSize;
  setDeviceType: React.Dispatch<React.SetStateAction<DeviceType>>;
  setScreenSize: React.Dispatch<React.SetStateAction<ScreenSize>>;
};

export const defaultContext: ContextType = {
  browserName: '',
  deviceType: {
    devicePixelRatio: 1,
    isDesktop: false,
    isLandscape: false,
    isMobile: true,
    isMobileWithOrientation: true,
    isPortrait: true,
    isTablet: false
  },
  nativeDeviceType: {
    isNative: false,
    platform: null
  },
  screenSize: {
    height: 0,
    width: 0
  },
  setDeviceType() {},
  setScreenSize() {}
};

const DeviceContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  browserName: string;
  deviceType: DeviceType;
  nativeDeviceType: NativeDeviceType;
};

export function DeviceContextProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const [browserName] = useState(props.browserName);
  const [deviceType, setDeviceType] = useState(props.deviceType);
  const [nativeDeviceType] = useState(props.nativeDeviceType);
  const [screenSize, setScreenSize] = useState(defaultContext.screenSize);

  const value = useMemo<ContextType>(
    () => ({
      browserName,
      deviceType,
      nativeDeviceType,
      screenSize,
      setDeviceType,
      setScreenSize
    }),
    [browserName, deviceType, nativeDeviceType, screenSize]
  );

  return (
    <DeviceContext.Provider value={value}>
      {props.children}
    </DeviceContext.Provider>
  );
}

DeviceContextProvider.displayName = 'DeviceContextProvider';

export const generateDeviceContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export const useDeviceType = (): DeviceType =>
  useDeepEqualMemo(useContext(DeviceContext).deviceType);

export const useIsMobile = (): boolean => useDeviceType().isMobile;

export const useIsMobileWithOrientation = (): boolean =>
  useDeviceType().isMobileWithOrientation;

export const useNativeDeviceType = (): NativeDeviceType =>
  useDeepEqualMemo(useContext(DeviceContext).nativeDeviceType);

export const useIsNative = (): boolean => useNativeDeviceType().isNative;

export const useIsNativeIOS = (): boolean => {
  const { isNative, platform } = useNativeDeviceType();

  return isNative && platform === 'ios';
};

export const useScreenSize = (): ScreenSize =>
  useDeepEqualMemo(useContext(DeviceContext).screenSize);

export function useIsSafari() {
  const browser = useContext(DeviceContext).browserName.toLowerCase();

  return browser.includes('safari') || browser.includes('ios');
}

export default DeviceContext;
